import { useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Edit2,
  ExternalLink,
  RefreshCw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAgentCreationLogDetails, useRestartAgentCreation } from "./hooks/useAgentCreationLogs";

import type { AgentCreationLog } from "@/types/agent-creation-logs";
interface AgentCreationLogDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLog: AgentCreationLog | null;
  getStatusBadge: (status: string | null) => React.ReactNode;
}

export const AgentCreationLogDetailsSidebar = ({
  isOpen,
  onClose,
  selectedLog,
  getStatusBadge,
}: AgentCreationLogDetailsSidebarProps) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [isEditingParams, setIsEditingParams] = useState(false);
  const [editedParams, setEditedParams] = useState<string>("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showRestartDialog, setShowRestartDialog] = useState(false);
  const [retryReason, setRetryReason] = useState("");
  const [isUserInfoExpanded, setIsUserInfoExpanded] = useState(false);
  const [isRequestParamsExpanded, setIsRequestParamsExpanded] = useState(false);

  const { data: detailsData } = useAgentCreationLogDetails(selectedLog?.id || 0);
  const restartMutation = useRestartAgentCreation();

  const handleOpenGenieDetails = () => {
    if (selectedLog?.agent_id) {
      window.open(`/genie-hub/my-genies/${selectedLog.agent_id}`, "_blank");
    }
  };

  const handleCopyParams = () => {
    if (selectedLog?.request_params) {
      const paramString = JSON.stringify(selectedLog.request_params, null, 2);
      navigator.clipboard.writeText(paramString);
      toast.success("Parameters copied to clipboard");
    }
  };

  const handleCopyUserId = () => {
    if (selectedLog?.user_id) {
      navigator.clipboard.writeText(selectedLog.user_id);
      toast.success("User ID copied to clipboard");
    }
  };

  const handleCopyPayload = (payload: any, type: "request" | "response") => {
    const payloadString = typeof payload === "string" ? payload : JSON.stringify(payload, null, 2);
    navigator.clipboard.writeText(payloadString);
    toast.success(`${type === "request" ? "Request" : "Response"} copied to clipboard`);
  };

  const handleCopyAllSteps = () => {
    if (detailsData?.data) {
      const filteredSteps = detailsData.data.map(({ id, log_id, ...rest }) => rest);
      const stepsString = JSON.stringify(filteredSteps, null, 2);
      navigator.clipboard.writeText(stepsString);
      toast.success("All execution steps copied to clipboard");
    }
  };

  const handleEditParams = () => {
    if (selectedLog?.request_params) {
      setEditedParams(JSON.stringify(selectedLog.request_params, null, 2));
      setJsonError(null);
      setIsEditingParams(true);
    }
  };

  const handleParamsChange = (newValue: string) => {
    setEditedParams(newValue);
    try {
      JSON.parse(newValue);
      setJsonError(null);
    } catch (e: any) {
      setJsonError(e.message);
    }
  };

  const toggleStepExpanded = (id: number) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleAllSteps = () => {
    if (expandedSteps.size === detailsData?.data.length) {
      setExpandedSteps(new Set());
    } else {
      const allIds = new Set(detailsData?.data.map((d) => d.id) || []);
      setExpandedSteps(allIds);
    }
  };

  const handleRestart = async () => {
    if (!selectedLog || jsonError) {
      toast.error("Invalid JSON parameters");
      return;
    }

    try {
      const params = JSON.parse(editedParams);
      await restartMutation.mutateAsync({
        original_log_id: selectedLog.id,
        edited_params: params,
        retry_reason: retryReason || undefined,
      });
      toast.success("Agent creation restart initiated");
      setIsEditingParams(false);
      setShowRestartDialog(false);
      setRetryReason("");
      onClose();
    } catch (error: any) {
      toast.error(`Restart failed: ${error.message}`);
    }
  };

  const canRestart =
    selectedLog && (selectedLog.status === "completed" || selectedLog.status === "failed");

  if (!selectedLog) return null;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent
          side="right"
          showClose={false}
          className="w-full sm:max-w-xl flex flex-col h-full p-0"
        >
          <SheetTitle className="sr-only">Agent Creation Details</SheetTitle>
          <SheetDescription className="sr-only">
            View and manage agent creation logs
          </SheetDescription>
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-genie-border">
              <div>
                <h2 className="text-xl font-bold text-genie-text-primary">
                  Agent Creation Details
                </h2>
                <p className="text-sm text-genie-text-secondary">Log ID: {selectedLog.id}</p>
              </div>
              <div className="flex items-center gap-1">
                {selectedLog?.agent_id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenGenieDetails}
                    title="Open genie details in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
                <SheetClose asChild>
                  <Button variant="ghost" size="sm" title="Close details">
                    <X className="h-4 w-4" />
                  </Button>
                </SheetClose>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-4 space-y-6">
                {/* Summary Cards */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-genie-text-primary">Summary</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                      <p className="text-xs text-genie-text-secondary">Agent Name</p>
                      <p className="font-medium text-genie-text-primary">
                        {selectedLog.agent_name || "—"}
                      </p>
                    </div>
                    <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                      <p className="text-xs text-genie-text-secondary">Status</p>
                      <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                    </div>
                    <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                      <p className="text-xs text-genie-text-secondary">Created</p>
                      <p className="text-sm font-medium text-genie-text-primary">
                        {new Date(selectedLog.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                      <p className="text-xs text-genie-text-secondary">Completed</p>
                      <p className="text-sm font-medium text-genie-text-primary">
                        {selectedLog.completed_at
                          ? new Date(selectedLog.completed_at).toLocaleString()
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* User Information */}
                <div className="border border-genie-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setIsUserInfoExpanded(!isUserInfoExpanded)}
                    className="w-full p-3 flex items-center justify-between hover:bg-genie-background transition-colors"
                  >
                    <h3 className="font-semibold text-genie-text-primary">User Information</h3>
                    {isUserInfoExpanded ? (
                      <ChevronUp className="h-4 w-4 text-genie-text-secondary" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-genie-text-secondary" />
                    )}
                  </button>

                  {isUserInfoExpanded && (
                    <div className="p-3 border-t border-genie-border space-y-2 bg-genie-background">
                      <div className="p-3 bg-white rounded-lg border border-genie-border">
                        <p className="text-xs text-genie-text-secondary">Email</p>
                        <p className="text-sm font-medium text-genie-text-primary break-all">
                          <CopyableEmail email={selectedLog.user_email} />
                        </p>
                      </div>
                      {selectedLog.user_full_name && (
                        <div className="p-3 bg-white rounded-lg border border-genie-border">
                          <p className="text-xs text-genie-text-secondary">Full Name</p>
                          <p className="text-sm font-medium text-genie-text-primary">
                            {selectedLog.user_full_name}
                          </p>
                        </div>
                      )}
                      <div className="p-3 bg-white rounded-lg border border-genie-border flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-xs text-genie-text-secondary">User ID</p>
                          <p className="text-xs font-mono text-genie-text-primary break-all">
                            {selectedLog.user_id || "—"}
                          </p>
                        </div>
                        {selectedLog.user_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCopyUserId}
                            title="Copy user ID to clipboard"
                            className="h-6 w-6 p-0 flex-shrink-0 mt-1"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Retry Info */}
                {selectedLog.retry_count! > 0 && (
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-700 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-yellow-900">Retry Attempt</p>
                        <p className="text-xs text-yellow-800">
                          Attempt #{selectedLog.retry_count}
                        </p>
                        {selectedLog.parent_log_id && (
                          <p className="text-xs text-yellow-800">
                            Parent Log ID: {selectedLog.parent_log_id}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Details */}
                {selectedLog.status === "failed" && selectedLog.error_message && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-medium text-red-900">Error</p>
                    <p className="text-xs text-red-800 mt-1">{selectedLog.error_message}</p>
                    {selectedLog.error_step && (
                      <p className="text-xs text-red-800 mt-1">Step: {selectedLog.error_step}</p>
                    )}
                  </div>
                )}

                {/* Request Parameters */}
                <div className="border border-genie-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setIsRequestParamsExpanded(!isRequestParamsExpanded)}
                    className="w-full p-3 flex items-center justify-between hover:bg-genie-background transition-colors"
                  >
                    <h3 className="font-semibold text-genie-text-primary">Request Parameters</h3>
                    {isRequestParamsExpanded ? (
                      <ChevronUp className="h-4 w-4 text-genie-text-secondary" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-genie-text-secondary" />
                    )}
                  </button>

                  {isRequestParamsExpanded && (
                    <div className="p-3 border-t border-genie-border space-y-2 bg-genie-background">
                      <div className="flex items-center gap-1 mb-2 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCopyParams}
                          title="Copy parameters to clipboard"
                          className="h-8"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        {!isEditingParams && canRestart && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleEditParams}
                            className="gap-2 h-8"
                          >
                            <Edit2 className="h-3 w-3" />
                            Edit
                          </Button>
                        )}
                      </div>

                      {isEditingParams ? (
                        <div className="space-y-2">
                          <textarea
                            value={editedParams}
                            onChange={(e) => handleParamsChange(e.target.value)}
                            className={cn(
                              "w-full h-64 p-3 font-mono text-sm rounded-lg border resize-none",
                              jsonError
                                ? "border-red-300 bg-red-50"
                                : "border-genie-border bg-genie-background",
                            )}
                          />
                          {jsonError && (
                            <p className="text-xs text-red-600">JSON Error: {jsonError}</p>
                          )}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => setShowRestartDialog(true)}
                              disabled={!!jsonError || restartMutation.isPending}
                              className="flex-1 gap-2"
                            >
                              {restartMutation.isPending && (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              )}
                              Restart
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setIsEditingParams(false);
                                setJsonError(null);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-white rounded-lg border border-genie-border font-mono text-xs max-h-64 overflow-y-auto">
                          {selectedLog.request_params ? (
                            <pre className="whitespace-pre-wrap break-words text-genie-text-secondary">
                              {JSON.stringify(selectedLog.request_params, null, 2)}
                            </pre>
                          ) : (
                            <p className="text-genie-text-muted">No parameters</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Step Details */}
                {detailsData?.data && detailsData.data.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-genie-text-primary">Execution Steps</h3>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCopyAllSteps}
                          title="Copy all execution steps to clipboard"
                          className="h-8"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={toggleAllSteps}
                          className="gap-2 h-8"
                        >
                          {expandedSteps.size === detailsData.data.length ? (
                            <>
                              <ChevronUp className="h-3 w-3" />
                              Collapse
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3" />
                              Expand
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {detailsData.data.map((detail) => (
                        <div key={detail.id} className="border border-genie-border rounded-lg">
                          <button
                            onClick={() => toggleStepExpanded(detail.id)}
                            className="w-full p-3 flex items-center justify-between hover:bg-genie-background transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 text-left">
                              {expandedSteps.has(detail.id) ? (
                                <ChevronUp className="h-4 w-4 text-genie-text-secondary flex-shrink-0" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-genie-text-secondary flex-shrink-0" />
                              )}
                              <div className="flex-1">
                                <p className="font-medium text-genie-text-primary">
                                  {detail.step_name}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge
                                    variant={
                                      detail.status === "completed"
                                        ? "default"
                                        : detail.status === "failed"
                                          ? "destructive"
                                          : "secondary"
                                    }
                                    className="text-xs"
                                  >
                                    {detail.status}
                                  </Badge>
                                  {detail.duration_ms && (
                                    <span className="text-xs text-genie-text-secondary">
                                      {detail.duration_ms}ms
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>

                          {expandedSteps.has(detail.id) && (
                            <div className="p-3 border-t border-genie-border bg-genie-background space-y-3 text-xs">
                              {detail.request_payload && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="font-medium text-genie-text-primary">Request</p>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        handleCopyPayload(detail.request_payload, "request")
                                      }
                                      title="Copy request to clipboard"
                                      className="h-6 w-6 p-0"
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <div className="p-2 bg-white rounded border border-genie-border font-mono overflow-x-auto">
                                    <pre className="whitespace-pre-wrap break-words text-genie-text-secondary">
                                      {typeof detail.request_payload === "string"
                                        ? detail.request_payload
                                        : JSON.stringify(detail.request_payload, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              )}
                              {detail.response_payload && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="font-medium text-genie-text-primary">Response</p>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        handleCopyPayload(detail.response_payload, "response")
                                      }
                                      title="Copy response to clipboard"
                                      className="h-6 w-6 p-0"
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <div className="p-2 bg-white rounded border border-genie-border font-mono overflow-x-auto">
                                    <pre className="whitespace-pre-wrap break-words text-genie-text-secondary">
                                      {typeof detail.response_payload === "string"
                                        ? detail.response_payload
                                        : JSON.stringify(detail.response_payload, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              )}
                              {detail.error_message && (
                                <div className="p-2 bg-red-50 rounded border border-red-200">
                                  <p className="font-medium text-red-900">Error</p>
                                  <p className="text-red-800 mt-1">{detail.error_message}</p>
                                </div>
                              )}
                              {detail.error_stack && (
                                <div className="p-2 bg-red-50 rounded border border-red-200">
                                  <p className="font-medium text-red-900">Error</p>
                                  <p className="text-red-800 mt-1">{detail.error_stack}</p>
                                </div>
                              )}
                              {detail.started_at && (
                                <div className="text-genie-text-secondary space-y-1">
                                  <p>
                                    <span className="font-medium">Started:</span>{" "}
                                    {new Date(detail.started_at).toLocaleTimeString()}
                                  </p>
                                  {detail.completed_at && (
                                    <p>
                                      <span className="font-medium">Completed:</span>{" "}
                                      {new Date(detail.completed_at).toLocaleTimeString()}
                                    </p>
                                  )}
                                  {detail.metadata?.callerFile && (
                                    <p className="text-xs">
                                      <span className="font-medium">File:</span>{" "}
                                      {detail.metadata.callerFile}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Restart Confirmation Dialog */}
      <AlertDialog open={showRestartDialog} onOpenChange={setShowRestartDialog}>
        <AlertDialogContent className="z-[80]">
          <AlertDialogTitle>Restart Agent Creation</AlertDialogTitle>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will create a new agent creation attempt with the modified parameters. The
              original log will be linked as parent.
            </p>
            <div>
              <label className="block text-sm font-medium text-genie-text-primary mb-2">
                Retry Reason (Optional)
              </label>
              <textarea
                value={retryReason}
                onChange={(e) => setRetryReason(e.target.value)}
                placeholder="Why are you restarting this creation?"
                className="w-full p-2 border border-genie-border rounded text-sm resize-none"
                rows={3}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestart} disabled={restartMutation.isPending}>
              {restartMutation.isPending && <RefreshCw className="h-4 w-4 animate-spin mr-2" />}
              Confirm Restart
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
